import { IconFromSet } from '@gettactic/components';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Group, Image, Text } from 'react-konva';
import { IconShape as IIconShape } from '../../../../types';
import { mergeRefs } from '../../../../utils/mergeRef';
import { getTextProps } from '../../helpers/getTextProps';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { ShapeCommonProps } from '../../types';
import { useShape } from '../ShapeContext/useShape';
import { IconShapeSVG } from './IconShapeSVG';

export interface IconShapeComponentProps extends ShapeCommonProps {
  renderMode?: 'konva' | 'svg';
}

export const IconShape = React.forwardRef<
  React.ElementRef<typeof Group>,
  IconShapeComponentProps
>(({ renderMode = 'konva', ...commonProps }, ref) => {
  const { isHovered, shape, isInSelection, image, setImage } = useShape();

  const iconShape = shape as IIconShape;
  const shapeRef = React.useRef<React.ElementRef<typeof Group>>(null);

  React.useEffect(() => {
    if (shape.type === 'icon' && shape.icon) {
      const svgString = ReactDOMServer.renderToStaticMarkup(
        <IconFromSet icon={shape.icon} color={shape.color} size={64} />
      );
      const img = new window.Image();

      img.onload = () => {
        setImage(img);
        getWindowKonvaObject().stages?.[0]?.fire?.('contentChange');
      };

      img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
    }
  }, [shape.type, (shape as IIconShape).icon, shape.color]);

  React.useEffect(() => {
    if (shape.type === 'icon' && image) {
      const layer = shapeRef.current?.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }
  }, [shape.type, image]);

  // If renderMode is SVG, return SVG components
  if (renderMode === 'svg') {
    return <IconShapeSVG />;
  }

  // Default Konva rendering
  return (
    <Group
      width={shape.width}
      height={shape.height}
      ref={mergeRefs([shapeRef, ref])}
      {...commonProps}
    >
      <Image
        image={image || undefined}
        width={shape.width}
        height={shape.height}
        opacity={shape.locked ? 0.5 : 1}
        imageSmoothingEnabled={true}
        perfectDrawEnabled={true}
      />
      {iconShape.text && (
        <Text
          text={iconShape.text}
          y={iconShape.height + 10}
          x={0}
          ref={(node) => {
            if (node) {
              const textWidth = node.width();
              if (iconShape.textAlign === 'center') {
                node.x((iconShape.width - textWidth) / 2);
              } else if (iconShape.textAlign === 'right') {
                node.x(iconShape.width - textWidth);
              } else {
                node.x(0);
              }
            }
          }}
          {...getTextProps(iconShape)}
          wrap={'none'}
        />
      )}
    </Group>
  );
});
